import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const TimeAnalysis = ({ data }) => {
  // Enhanced gradient creation for line fill
  const createGradient = (ctx, chartArea) => {
    const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
    gradient.addColorStop(0, 'rgba(107, 115, 255, 0.05)');
    gradient.addColorStop(0.5, 'rgba(107, 115, 255, 0.15)');
    gradient.addColorStop(1, 'rgba(107, 115, 255, 0.3)');
    return gradient;
  };

  const chartData = {
    labels: data.map(item => {
      const date = new Date(item._id);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }),
    datasets: [
      {
        label: 'Average Time (minutes)',
        data: data.map(item => Math.round(item.avgTime)),
        borderColor: '#6b73ff',
        backgroundColor: function(context) {
          const chart = context.chart;
          const {ctx, chartArea} = chart;
          if (!chartArea) {
            return null;
          }
          return createGradient(ctx, chartArea);
        },
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#6b73ff',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 3,
        pointRadius: 6,
        pointHoverRadius: 8,
        pointHoverBackgroundColor: '#4c46ef',
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 4,
        // Add shadow effect for points
        shadowOffsetX: 0,
        shadowOffsetY: 2,
        shadowBlur: 8,
        shadowColor: 'rgba(107, 115, 255, 0.3)',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    animation: {
      duration: 800,
      easing: 'easeOutQuart',
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1f2937',
        bodyColor: '#4b5563',
        borderColor: 'rgba(107, 115, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 12,
        displayColors: false,
        padding: 12,
        titleFont: {
          size: 14,
          weight: '600',
        },
        bodyFont: {
          size: 13,
          weight: '500',
        },
        callbacks: {
          title: function(context) {
            return context[0].label;
          },
          label: function(context) {
            return `Average Time: ${context.parsed.y} minutes`;
          },
          afterLabel: function(context) {
            // Calculate trend if we have multiple data points
            const dataPoints = context.dataset.data;
            const currentIndex = context.dataIndex;
            if (currentIndex > 0) {
              const current = dataPoints[currentIndex];
              const previous = dataPoints[currentIndex - 1];
              const change = current - previous;
              const trend = change > 0 ? '↗️' : change < 0 ? '↘️' : '➡️';
              const changeText = change > 0 ? `+${change}` : `${change}`;
              return `${trend} ${changeText} min from previous`;
            }
            return null;
          },
        },
        external: function(context) {
          // Custom tooltip styling for dark mode
          const tooltip = context.tooltip;
          if (document.documentElement.classList.contains('dark')) {
            context.tooltip.options.backgroundColor = 'rgba(31, 41, 55, 0.95)';
            context.tooltip.options.titleColor = '#f9fafb';
            context.tooltip.options.bodyColor = '#d1d5db';
            context.tooltip.options.borderColor = 'rgba(107, 115, 255, 0.3)';
          }
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Time (minutes)',
          color: '#6b7280',
          font: {
            size: 12,
            weight: '600',
          },
          padding: { top: 10, bottom: 10 },
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 12,
            weight: '500',
          },
          padding: 8,
          callback: function(value) {
            return `${value}m`;
          },
        },
        grid: {
          color: function(context) {
            return document.documentElement.classList.contains('dark') 
              ? 'rgba(75, 85, 99, 0.3)' 
              : 'rgba(229, 231, 235, 0.8)';
          },
          lineWidth: 1,
          drawBorder: false,
        },
        border: {
          display: false,
        },
      },
      x: {
        title: {
          display: true,
          text: 'Date',
          color: '#6b7280',
          font: {
            size: 12,
            weight: '600',
          },
          padding: { top: 10, bottom: 10 },
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 12,
            weight: '500',
          },
          maxRotation: 45,
          minRotation: 0,
          padding: 8,
        },
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
      },
    },
    elements: {
      point: {
        hoverBackgroundColor: '#4c46ef',
        hoverBorderColor: '#ffffff',
      },
      line: {
        borderJoinStyle: 'round',
        borderCapStyle: 'round',
      },
    },
    // Add hover animations
    onHover: (event, activeElements) => {
      event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
    },
  };

  // Calculate statistics
  const avgTimes = data.map(item => item.avgTime);
  const totalAverage = avgTimes.length > 0 ? 
    Math.round(avgTimes.reduce((sum, time) => sum + time, 0) / avgTimes.length) : 0;
  const minTime = avgTimes.length > 0 ? Math.round(Math.min(...avgTimes)) : 0;
  const maxTime = avgTimes.length > 0 ? Math.round(Math.max(...avgTimes)) : 0;

  // Calculate trend
  const getTrend = () => {
    if (avgTimes.length < 2) return null;
    const recent = avgTimes.slice(-3); // Last 3 data points
    const older = avgTimes.slice(0, -3);
    if (older.length === 0) return null;
    
    const recentAvg = recent.reduce((sum, time) => sum + time, 0) / recent.length;
    const olderAvg = older.reduce((sum, time) => sum + time, 0) / older.length;
    
    if (recentAvg > olderAvg + 2) return { direction: 'up', text: 'Increasing' };
    if (recentAvg < olderAvg - 2) return { direction: 'down', text: 'Decreasing' };
    return { direction: 'stable', text: 'Stable' };
  };

  const trend = getTrend();

  return (
    <div className="card">
      {/* Enhanced Header */}
      <div className="card-header">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Time Analysis
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Average time spent solving problems over time
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-gradient">
              {totalAverage}m
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Overall Average
            </div>
          </div>
        </div>
      </div>

      {/* Chart Container */}
      <div className="card-body">
        <div className="h-72 relative">
          <Line data={chartData} options={options} />
          
          {/* Overlay gradient for visual enhancement */}
          <div className="absolute inset-0 pointer-events-none rounded-2xl bg-gradient-to-t from-white/5 to-transparent dark:from-gray-900/10"></div>
        </div>

        {/* Enhanced Stats Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="flex items-center justify-center space-x-2 mb-1">
                <div className="w-2 h-2 bg-gradient-to-r from-green-500 to-green-600 rounded-full"></div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Best Time</span>
              </div>
              <div className="text-lg font-bold text-gradient">
                {minTime}m
              </div>
            </div>
            <div>
              <div className="flex items-center justify-center space-x-2 mb-1">
                <div className="w-2 h-2 bg-gradient-to-r from-red-500 to-red-600 rounded-full"></div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Longest Time</span>
              </div>
              <div className="text-lg font-bold text-gradient">
                {maxTime}m
              </div>
            </div>
            <div>
              <div className="flex items-center justify-center space-x-2 mb-1">
                <div className="w-2 h-2 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full"></div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Trend</span>
              </div>
              <div className="text-lg font-bold text-gradient">
                {trend ? trend.text : 'N/A'}
              </div>
            </div>
          </div>
        </div>

        {/* Additional Insight */}
        {trend && (
          <div className="mt-4 pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
            <div className="flex items-center justify-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${
                trend.direction === 'up' ? 'bg-red-500' : 
                trend.direction === 'down' ? 'bg-green-500' : 'bg-blue-500'
              }`}></div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Your solving time has been{' '}
                <span className={`font-semibold ${
                  trend.direction === 'up' ? 'text-red-600 dark:text-red-400' : 
                  trend.direction === 'down' ? 'text-green-600 dark:text-green-400' : 
                  'text-blue-600 dark:text-blue-400'
                }`}>
                  {trend.text.toLowerCase()}
                </span>
                {' '}recently
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeAnalysis;